import React, {useState, useEffect} from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  Switch,
  FormControlLabel,
  CircularProgress,
  Alert,
  Autocomplete,
  Chip,
  Checkbox,
  Box,
} from '@mui/material';
import {
  CheckBox as CheckBoxIcon,
  CheckBoxOutlineBlank as CheckBoxBlankIcon,
} from '@mui/icons-material';
import MDButton from 'components/MDButton';
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import {useServicePlanApi} from '../services/servicePlanApi';
import {categoriesApi} from 'api/categoriesApi';
import {Notify} from 'notiflix';
import axios from 'axios';
import {ENDPOINT} from 'constants';

// Theme accent (matches ServicePlanManager pages)
const ACCENT = '#667eea';
const ACCENT2 = '#764ba2';

function CreateQuestionnaireModal({open, onClose, questionnaire, onSave}) {
  const {questionnairesApi} = useServicePlanApi();

  const [formData, setFormData] = useState({
    pageId: '',
    name: '',
    description: '',
    ctaButtonText: 'Get Started',
    ctaButtonPosition: 'hero',
    completionMessage: 'Thanks! Here are your recommended plans.',
    categoryIds: [],
    isActive: true,
  });

  const [pages, setPages] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fetchingPages, setFetchingPages] = useState(false);
  const [fetchingCategories, setFetchingCategories] = useState(false);

  const safePages = Array.isArray(pages) ? pages : [];
  const safeCategories = Array.isArray(categories) ? categories : [];

  useEffect(() => {
    if (open) {
      fetchPages();
      fetchCategories();
      if (questionnaire) {
        // Edit mode
        setFormData({
          pageId: questionnaire.pageId || '',
          name: questionnaire.name || '',
          description: questionnaire.description || '',
          ctaButtonText: questionnaire.ctaButtonText || 'Get Started',
          ctaButtonPosition: questionnaire.ctaButtonPosition || 'hero',
          completionMessage:
            questionnaire.completionMessage ||
            'Thanks! Here are your recommended plans.',
          categoryIds: Array.isArray(questionnaire.meta?.categoryIds)
            ? questionnaire.meta.categoryIds
            : Array.isArray(questionnaire.categoryIds)
            ? questionnaire.categoryIds
            : [],
          isActive:
            questionnaire.isActive !== undefined
              ? questionnaire.isActive
              : true,
        });
      } else {
        // Create mode - reset
        setFormData({
          pageId: '',
          name: '',
          description: '',
          ctaButtonText: 'Get Started',
          ctaButtonPosition: 'hero',
          completionMessage: 'Thanks! Here are your recommended plans.',
          categoryIds: [],
          isActive: true,
        });
      }
    }
  }, [open, questionnaire]);

  const fetchPages = async () => {
    setFetchingPages(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${ENDPOINT}api/v1/admin/page/list`, {
        headers: {Authorization: `Bearer ${token}`},
      });
      const pagesData = Array.isArray(response?.data?.data?.pages)
        ? response.data.data.pages
        : Array.isArray(response?.data?.data)
        ? response.data.data
        : Array.isArray(response?.data?.pages)
        ? response.data.pages
        : Array.isArray(response?.data)
        ? response.data
        : [];

      setPages(pagesData);
    } catch (error) {
      console.error('Failed to fetch pages:', error);
      setPages([]);
      Notify.failure('Failed to load pages');
    } finally {
      setFetchingPages(false);
    }
  };

  const fetchCategories = async () => {
    setFetchingCategories(true);
    try {
      const data = await categoriesApi.fetchAllActiveCategories();
      setCategories(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Failed to fetch categories:', error);
      setCategories([]);
      Notify.failure('Failed to load feature categories');
    } finally {
      setFetchingCategories(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({...prev, [field]: value}));
  };

  const handleSubmit = async () => {
    if (!formData.pageId) {
      Notify.failure('Please select a page');
      return;
    }

    if (!formData.name.trim()) {
      Notify.failure('Please enter a questionnaire name');
      return;
    }

    setLoading(true);
    try {
      if (questionnaire) {
        // Update
        await questionnairesApi.update(questionnaire.id, formData);
        Notify.success('Questionnaire updated successfully');
      } else {
        // Create
        await questionnairesApi.create(formData);
        Notify.success('Questionnaire created successfully');
      }
      onSave();
    } catch (error) {
      console.error('Failed to save questionnaire:', error);
      Notify.failure(
        error.response?.data?.message || 'Failed to save questionnaire',
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {/* {questionnaire ? 'Edit Questionnaire' : 'Create Questionnaire'} */}
      </DialogTitle>
      <DialogContent>
        <MDBox pt={2}>
          <Alert severity="info" sx={{mb: 3}}>
            {questionnaire
              ? 'Update questionnaire settings. Page cannot be changed after creation.'
              : 'Link a questionnaire to a page. Each page can have only one questionnaire.'}
          </Alert>

          {/* Page Selection */}
          <FormControl fullWidth sx={{mb: 2}}>
            <Autocomplete
              options={safePages.filter(p => p?.active)}
              getOptionLabel={option => `${option.name} (${option.route})`}
              value={safePages.find(p => p.id === formData.pageId) || null}
              onChange={(e, newValue) =>
                handleChange('pageId', newValue?.id || '')
              }
              loading={fetchingPages}
              disabled={!!questionnaire} // Can't change page when editing
              renderInput={params => (
                <TextField
                  {...params}
                  label="Select Page *"
                  placeholder="Choose a page..."
                  helperText={
                    questionnaire
                      ? 'Page cannot be changed'
                      : 'Select which page this questionnaire belongs to'
                  }
                />
              )}
            />
          </FormControl>

          {/* Name */}
          <TextField
            fullWidth
            label="Questionnaire Name *"
            value={formData.name}
            onChange={e => handleChange('name', e.target.value)}
            placeholder="e.g., SEO Service Assessment"
            helperText="Internal name for this questionnaire"
            sx={{mb: 2}}
          />

          {/* Description */}
          <TextField
            fullWidth
            label="Description"
            value={formData.description}
            onChange={e => handleChange('description', e.target.value)}
            multiline
            rows={3}
            placeholder="Brief description of what this questionnaire assesses..."
            helperText="Optional: Describe the purpose of this questionnaire"
            sx={{mb: 2}}
          />

          {/* Feature Categories */}
          <FormControl fullWidth sx={{mb: 2}}>
            <Autocomplete
              multiple
              disableCloseOnSelect
              options={safeCategories}
              getOptionLabel={option => option?.name || ''}
              isOptionEqualToValue={(option, val) => option.id === val.id}
              value={safeCategories.filter(c =>
                (formData.categoryIds || []).includes(c.id),
              )}
              onChange={(e, newValue) =>
                handleChange(
                  'categoryIds',
                  (newValue || []).map(c => c.id),
                )
              }
              loading={fetchingCategories}
              renderOption={(props, option, {selected}) => (
                <Box
                  component="li"
                  {...props}
                  sx={{
                    borderRadius: 1.5,
                    mx: 0.5,
                    my: 0.2,
                    '&[aria-selected="true"]': {
                      bgcolor: `${ACCENT}14 !important`,
                    },
                    '&.Mui-focused': {bgcolor: `${ACCENT}0d !important`},
                  }}>
                  <Checkbox
                    icon={<CheckBoxBlankIcon fontSize="small" />}
                    checkedIcon={<CheckBoxIcon fontSize="small" />}
                    checked={selected}
                    sx={{
                      mr: 1,
                      color: '#c4c9d4',
                      '&.Mui-checked': {color: ACCENT},
                    }}
                  />
                  <span
                    style={{
                      fontSize: 14,
                      fontWeight: selected ? 600 : 400,
                      color: selected ? ACCENT : '#374151',
                    }}>
                    {option.name}
                  </span>
                </Box>
              )}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    {...getTagProps({index})}
                    key={option.id}
                    label={option.name}
                    size="small"
                    sx={{
                      background: `linear-gradient(135deg, ${ACCENT}, ${ACCENT2})`,
                      color: '#fff',
                      fontWeight: 600,
                      borderRadius: 1.5,
                      '& .MuiChip-deleteIcon': {
                        color: 'rgba(255,255,255,0.8)',
                        '&:hover': {color: '#fff'},
                      },
                    }}
                  />
                ))
              }
              renderInput={params => (
                <TextField
                  {...params}
                  label="Feature Categories"
                  placeholder="Select categories..."
                  helperText="Only features from these categories will be available when creating mappings. Leave empty to allow all."
                />
              )}
              sx={{
                '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline':
                  {borderColor: ACCENT},
                '& .MuiInputLabel-root.Mui-focused': {color: ACCENT},
              }}
            />
          </FormControl>

          {/* Completion Message */}
          <TextField
            fullWidth
            label="Completion Message"
            value={formData.completionMessage}
            onChange={e => handleChange('completionMessage', e.target.value)}
            multiline
            rows={2}
            placeholder="Thanks! Here are your recommended plans."
            helperText="Message shown after user completes the questionnaire"
            sx={{mb: 2}}
          />

          {/* Active Status */}
          <FormControlLabel
            control={
              <Switch
                checked={formData.isActive}
                onChange={e => handleChange('isActive', e.target.checked)}
              />
            }
            label={
              <MDBox>
                <MDTypography variant="button">Active</MDTypography>
                <MDTypography
                  variant="caption"
                  color="text.secondary"
                  display="block">
                  {formData.isActive
                    ? 'Users can see and access this questionnaire'
                    : 'Questionnaire is hidden from users'}
                </MDTypography>
              </MDBox>
            }
          />
        </MDBox>
      </DialogContent>
      <DialogActions>
        <MDButton onClick={onClose} color="secondary" disabled={loading}>
          Cancel
        </MDButton>
        <MDButton onClick={handleSubmit} color="info" disabled={loading}>
          {loading ? (
            <CircularProgress size={20} color="inherit" />
          ) : questionnaire ? (
            'Update'
          ) : (
            'Create'
          )}
        </MDButton>
      </DialogActions>
    </Dialog>
  );
}

export default CreateQuestionnaireModal;
